import { Clock } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "./beui/Button";
import { WheelPicker } from "./beui/WheelPicker";

const HOURS = Array.from({ length: 12 }, (_, hour) => String(hour + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, minute) => String(minute).padStart(2, "0"));
type TimeWheelInputProps = {
  id?: string;
  label: string;
  dialogLabel?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

/** Controlled HH:mm or empty. This control chooses one wall-clock time; callers
 * retain range, overnight and timezone policy. Done/Clear are explicit changes. */
export function TimeWheelInput({ id, label, dialogLabel = `${label} time`, value, onChange, disabled = false }: TimeWheelInputProps) {
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const [open, setOpen] = useState(false);
  const displayValue = value ? `${Number(value.slice(0, 2)) % 12 || 12}:${value.slice(3, 5)} ${Number(value.slice(0, 2)) < 12 ? "AM" : "PM"}` : "";
  function commit(next: string) { onChange(next); setOpen(false); }
  return <div className="min-w-0">
    <label htmlFor={triggerId} className="mb-1.5 block text-xs text-mut">{label}</label>
    <Button id={triggerId} type="button" variant="outline" className="min-h-11 w-full justify-between gap-2 border-line bg-white px-3 text-sm font-normal text-navy" pressScale={0.98}
      aria-label={`${dialogLabel}, ${displayValue || "not set"}`} aria-haspopup="dialog" aria-expanded={open} disabled={disabled} onClick={() => setOpen(true)}>
      <span className="truncate tabular-nums">{displayValue || "--:--"}</span><Clock className="h-4 w-4 shrink-0 text-mut" aria-hidden="true" />
    </Button>
    {open && <TimeWheels label={dialogLabel} value={value} onCancel={() => setOpen(false)} onCommit={commit} />}
  </div>;
}

function TimeWheels({ label, value, onCancel, onCommit }: { label: string; value: string; onCancel: () => void; onCommit: (value: string) => void }) {
  const [hour, setHour] = useState(() => String(Number(value.slice(0, 2)) % 12 || 12).padStart(2, "0"));
  const [minute, setMinute] = useState(() => value ? value.slice(3, 5) : "00");
  const [period, setPeriod] = useState(() => Number(value.slice(0, 2)) >= 12 ? "PM" : "AM");
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  useEffect(() => {
    const modal = dialog.current;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modal?.showModal(); heading.current?.focus({ preventScroll: true });
    return () => { modal?.close(); if (opener?.isConnected) opener.focus({ preventScroll: true }); };
  }, []);

  function trapFocus(event: KeyboardEvent<HTMLDialogElement>) {
    if (event.key !== "Tab") return;
    const controls = [...(dialog.current?.querySelectorAll<HTMLElement>("button:not(:disabled):not([tabindex='-1']), [tabindex='0']") ?? [])].filter((control) => control.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === heading.current)) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  return <dialog ref={dialog} aria-labelledby={titleId} onKeyDown={trapFocus} onCancel={(event) => { event.preventDefault(); onCancel(); }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onCancel();
    }}
    className="m-auto max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xs overflow-y-auto rounded-3xl border border-line bg-white p-5 shadow-xl backdrop:bg-navy/50 sm:p-6">
    <h2 ref={heading} id={titleId} tabIndex={-1} className="text-lg font-bold text-navy outline-none">{label}</h2>
    <div className="mt-4 flex items-center gap-2">
      <WheelPicker aria-label="Hour, 12-hour time" className="min-w-0 flex-1" options={HOURS} value={hour} onValueChange={setHour} itemHeight={44} sound />
      <span className="text-xl font-semibold text-mut" aria-hidden="true">:</span>
      <WheelPicker aria-label="Minute" className="min-w-0 flex-1" options={MINUTES} value={minute} onValueChange={setMinute} itemHeight={44} sound />
      <WheelPicker aria-label="AM or PM" className="min-w-0 flex-1" options={["AM", "PM"]} value={period} onValueChange={setPeriod} itemHeight={44} sound />
    </div>
    <div className="mt-3 flex justify-end">
      {value && <Button type="button" variant="ghost" className="min-h-11 px-2 text-mut" pressScale={0.98} onClick={() => onCommit("")}>Clear</Button>}
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3">
      <Button type="button" variant="outline" className="min-h-11 bg-white" pressScale={0.98} onClick={onCancel}>Cancel</Button>
      <Button type="button" className="min-h-11 bg-navy text-white hover:bg-navy-500" pressScale={0.98} onClick={() => onCommit(`${String(Number(hour) % 12 + (period === "PM" ? 12 : 0)).padStart(2, "0")}:${minute}`)}>Done</Button>
    </div>
  </dialog>;
}
